// Sun shadow map — depth-only render from the sun's perspective.
// Single cascade orthographic projection covering the scene's voxel bounds.
// Sampled in the lighting pass with PCF for smooth shadow edges.

import type { GPU } from "./gpu.js";
import { POS_NORM_LAYOUT, INSTANCE_LAYOUT } from "./deferred.js";
import { mat4LookAt, mat4Ortho, mat4Multiply, type Mat4 } from "./math3d.js";

const SHADOW_RES = 2048;

const SHADOW_SHADER = /* wgsl */ `
struct Uniforms { lightViewProj: mat4x4f };
@group(0) @binding(0) var<uniform> u: Uniforms;

@vertex fn vsShadowInstanced(
  @location(0) vp: vec3f, @location(1) vn: vec3f,
  @location(6) iPos: vec3f, @location(7) iScaleX: f32,
  @location(8) iScaleYZ: vec2f,
  @location(9) iColor: vec3f, @location(10) iEmissive: f32, @location(11) iRoughness: f32,
) -> @builtin(position) vec4f {
  let scale = vec3f(iScaleX, iScaleYZ.x, iScaleYZ.y);
  return u.lightViewProj * vec4f(iPos + vp * scale, 1.0);
}

@vertex fn vsShadowStatic(
  @location(0) vp: vec3f, @location(1) vn: vec3f,
) -> @builtin(position) vec4f {
  return u.lightViewProj * vec4f(vp, 1.0);
}
`;

export class SunShadowPass {
  depthTexture: GPUTexture;
  lightViewProj = new Float32Array(16);

  private instancedPipeline: GPURenderPipeline;
  private staticPipeline: GPURenderPipeline;
  private uniformBuffer: GPUBuffer;
  private bindGroup: GPUBindGroup;

  // Cache — skip re-render when scene is static
  private lastSunDir: [number, number, number] = [0, 0, 0];
  private lastInstCount = -1;
  private _dirty = true;

  constructor(private gpu: GPU) {
    const { device } = gpu;

    this.depthTexture = device.createTexture({
      size: [SHADOW_RES, SHADOW_RES],
      format: "depth32float",
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });

    const module = device.createShaderModule({ code: SHADOW_SHADER });
    this.uniformBuffer = device.createBuffer({ size: 64, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });

    const bgLayout = device.createBindGroupLayout({
      entries: [{ binding: 0, visibility: GPUShaderStage.VERTEX, buffer: { type: "uniform" } }],
    });
    const pipeLayout = device.createPipelineLayout({ bindGroupLayouts: [bgLayout] });

    const depthStencil: GPUDepthStencilState = {
      format: "depth32float",
      depthWriteEnabled: true,
      depthCompare: "less",
      depthBias: 2,
      depthBiasSlopeScale: 1.5,
    };

    this.instancedPipeline = device.createRenderPipeline({
      layout: pipeLayout,
      vertex: { module, entryPoint: "vsShadowInstanced", buffers: [POS_NORM_LAYOUT, INSTANCE_LAYOUT] },
      depthStencil,
      primitive: { topology: "triangle-list", cullMode: "back" },
    });

    this.staticPipeline = device.createRenderPipeline({
      layout: pipeLayout,
      vertex: { module, entryPoint: "vsShadowStatic", buffers: [POS_NORM_LAYOUT] },
      depthStencil,
      primitive: { topology: "triangle-list", cullMode: "back" },
    });

    this.bindGroup = device.createBindGroup({
      layout: bgLayout,
      entries: [{ binding: 0, resource: { buffer: this.uniformBuffer } }],
    });
  }

  markDirty(): void { this._dirty = true; }

  render(
    encoder: GPUCommandEncoder,
    sunDir: [number, number, number],
    voxelBounds: { min: [number, number, number]; max: [number, number, number] },
    cubeVertBuffer: GPUBuffer,
    instanceBuffer: GPUBuffer,
    instCount: number,
    meshVertBuffer: GPUBuffer | null,
    meshVertCount: number,
  ): void {
    // Skip re-render if sun direction and instance count haven't changed
    const [sx, sy, sz] = sunDir;
    if (sx === this.lastSunDir[0] && sy === this.lastSunDir[1] && sz === this.lastSunDir[2] && instCount === this.lastInstCount && !this._dirty) {
      return;
    }
    this.lastSunDir = [sx, sy, sz];
    this.lastInstCount = instCount;
    this._dirty = false;

    const sunLen = Math.sqrt(sx * sx + sy * sy + sz * sz);
    if (sunLen < 0.001) return;
    const lx = sx / sunLen, ly = sy / sunLen, lz = sz / sunLen;

    const bMin = voxelBounds.min, bMax = voxelBounds.max;
    const cx = (bMin[0] + bMax[0]) * 0.5;
    const cy = (bMin[1] + bMax[1]) * 0.5;
    const cz = (bMin[2] + bMax[2]) * 0.5;
    const ex = (bMax[0] - bMin[0]) * 0.5;
    const ey = (bMax[1] - bMin[1]) * 0.5;
    const ez = (bMax[2] - bMin[2]) * 0.5;
    const extent = Math.sqrt(ex * ex + ey * ey + ez * ez);

    const eye: [number, number, number] = [cx + lx * extent, cy + ly * extent, cz + lz * extent];
    const target: [number, number, number] = [cx, cy, cz];
    const view = mat4LookAt(eye, target, [0, 1, 0]);

    // Transform AABB corners into light-view space to get tight ortho bounds
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;
    for (let i = 0; i < 8; i++) {
      const wx = (i & 1) ? bMax[0] : bMin[0];
      const wy = (i & 2) ? bMax[1] : bMin[1];
      const wz = (i & 4) ? bMax[2] : bMin[2];
      // Transform by view matrix (column-major)
      const vx = view[0] * wx + view[4] * wy + view[8] * wz + view[12];
      const vy = view[1] * wx + view[5] * wy + view[9] * wz + view[13];
      const vz = view[2] * wx + view[6] * wy + view[10] * wz + view[14];
      minX = Math.min(minX, vx); maxX = Math.max(maxX, vx);
      minY = Math.min(minY, vy); maxY = Math.max(maxY, vy);
      minZ = Math.min(minZ, vz); maxZ = Math.max(maxZ, vz);
    }

    // View-space Z is negative (camera looks in -Z). Ortho expects positive near/far.
    const proj = mat4Ortho(minX, maxX, minY, maxY, -maxZ, -minZ);
    const lvp = mat4Multiply(proj, view);
    this.lightViewProj.set(lvp);

    this.gpu.device.queue.writeBuffer(this.uniformBuffer, 0, this.lightViewProj);

    // Depth-only render pass
    const pass = encoder.beginRenderPass({
      colorAttachments: [],
      depthStencilAttachment: {
        view: this.depthTexture.createView(),
        depthClearValue: 1.0,
        depthLoadOp: "clear",
        depthStoreOp: "store",
      },
    });

    // Instanced boxes
    if (instCount > 0) {
      pass.setPipeline(this.instancedPipeline);
      pass.setBindGroup(0, this.bindGroup);
      pass.setVertexBuffer(0, cubeVertBuffer);
      pass.setVertexBuffer(1, instanceBuffer);
      pass.draw(36, instCount);
    }

    // Static mesh
    if (meshVertBuffer && meshVertCount > 0) {
      pass.setPipeline(this.staticPipeline);
      pass.setBindGroup(0, this.bindGroup);
      pass.setVertexBuffer(0, meshVertBuffer);
      pass.draw(meshVertCount);
    }

    pass.end();
  }
}
